/**
 * useCurrentOrg — the currently-selected org for this browser session.
 *
 * Source of truth for the AVAILABLE orgs is the authenticated session
 * (`useAuth().session.orgs`, populated from `/auth/userInfo`'s
 * `organizationMemberships`) — NOT a fixture. The previous implementation read
 * a hardcoded `@shared/data/orgs` list, so the switcher showed fake demo orgs
 * (Acme Security, Northwind Labs, …) instead of the user's real tenants and
 * switching never changed any data.
 *
 * A small module-level store holds only the SELECTED org id (a string), so
 * every consumer (header `OrgSwitcher`, `IdentityStrip`, org-scoped data hooks)
 * re-renders together when the org changes in the same tab, and a hard reload /
 * second tab stay aligned via localStorage + the `storage` event. The org
 * OBJECTS always come from the live session, so the selected id resolves
 * against the user's real memberships (stale ids fall back to the default org).
 *
 * `useCurrentOrgId()` is the canonical accessor for the active org id in query
 * keys and request params — every org-scoped read goes through it (never
 * `useAuth().session?.org?.id`, which is the user's primary org, not the
 * actively-selected tenant).
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth, roleForOrg, type AuthOrg, type UserRole } from '@shared/auth';

const STORAGE_KEY = 'armoriq:current-org:v1';

/** Sentinel returned when there is no resolvable org (no session yet). */
export const NO_ORG_KEY = 'unknown';

/** The shape consumers read. `key` is the real org id (UUID), or NO_ORG_KEY. */
export type CurrentOrg = { key: string; name: string; createdAt?: string };

/* ───────────────────────── module-level store ─────────────────────────
 * Holds ONLY the selected org id. Validation against real memberships happens
 * in the hook (which has the session); the store stays free of React context. */

function readStoredId(): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

let selectedId: string = readStoredId();
let selectedByOtherTab = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // `storage` fires only in OTHER tabs, covering the multi-window case;
  // same-tab updates go through emit() in setSelectedId directly.
  const onStorage = (e: StorageEvent): void => {
    if (e.key !== STORAGE_KEY) return;
    const next = e.newValue ?? '';
    if (next !== selectedId) {
      selectedId = next;
      selectedByOtherTab = true;
      emit();
    }
  };
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
}

function getSnapshot(): string {
  return selectedId;
}

function getServerSnapshot(): string {
  return '';
}

function setSelectedId(nextId: string): void {
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(STORAGE_KEY, nextId);
    } catch {
      // Silent: same fallthrough policy as the rest of our localStorage hooks.
    }
  }
  selectedByOtherTab = false;
  if (nextId === selectedId) return;
  selectedId = nextId;
  emit();
}

/**
 * Forget the actively-selected org (localStorage + in-memory store).
 *
 * Called when the session ends so the selection can't leak into the next one:
 * logout used to clear only the tokens, leaving `armoriq:current-org:v1`
 * behind, so a later login — even a DIFFERENT user on the same browser — could
 * inherit the previous tenant (and `apiClient` would send that org's
 * `x-organization-id`). Clearing here resets both the persisted id and the
 * module store, so `getCurrentOrgId()` and every consumer fall back to the new
 * session's primary org.
 *
 * Wired via `window` events (below) rather than a direct call from
 * `auth-provider`, because `use-current-org` already depends on `@shared/auth`
 * for the session — a back-import would be a cycle.
 */
export function clearCurrentOrg(): void {
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Silent: same fallthrough policy as the rest of our localStorage hooks.
    }
  }
  selectedByOtherTab = false;
  if (selectedId === '') return;
  selectedId = '';
  emit();
}

// Session-end signals clear the selection. `auth:signout` is dispatched by
// AuthProvider.signOut; `auth:expired` by apiClient on a terminal
// 401→refresh-failure. Both mean "this session is over" — registered at module
// scope so the reset fires regardless of which components are mounted.
if (typeof window !== 'undefined') {
  window.addEventListener('auth:signout', clearCurrentOrg);
  window.addEventListener('auth:expired', clearCurrentOrg);
}

function findOrg(orgs: AuthOrg[], id: string | null | undefined): AuthOrg | undefined {
  return id ? orgs.find((o) => o.id === id) : undefined;
}

function toCurrentOrg(o: AuthOrg): CurrentOrg {
  return { key: o.id, name: o.name, ...(o.createdAt ? { createdAt: o.createdAt } : {}) };
}

export type UseCurrentOrgResult = {
  /** The resolved active org. `key` is NO_ORG_KEY when no session/org exists. */
  org: CurrentOrg;
  /** Switch the active org for the whole tab (pass a real org id). */
  setOrg: (orgId: string) => void;
  /** The user's real orgs from the session (empty before auth resolves). */
  orgs: CurrentOrg[];
};

export function useCurrentOrg(): UseCurrentOrgResult {
  const { session } = useAuth();
  const storedId = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const authOrgs = useMemo<AuthOrg[]>(() => session?.orgs ?? [], [session?.orgs]);

  const resolved = useMemo<AuthOrg | undefined>(
    () =>
      findOrg(authOrgs, storedId) ??
      findOrg(authOrgs, session?.platformDefaultOrgId) ??
      authOrgs[0],
    [authOrgs, storedId, session?.platformDefaultOrgId]
  );

  const setOrg = useCallback((orgId: string) => setSelectedId(orgId), []);

  return {
    org: resolved ? toCurrentOrg(resolved) : { key: NO_ORG_KEY, name: 'Organization' },
    setOrg,
    orgs: authOrgs.map(toCurrentOrg),
  };
}

/**
 * Write the resolved org to the per-browser store so `x-organization-id` goes out
 * before the user touches the switcher. Mount once, high in the tree. An id that
 * another tab stored is kept even when this session does not list it yet.
 */
export function usePersistCurrentOrg(): void {
  const resolvedId = useCurrentOrgId();
  const storedId = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  useLayoutEffect(() => {
    if (!resolvedId || resolvedId === storedId) return;
    if (storedId && selectedByOtherTab) return;
    setSelectedId(resolvedId);
  }, [resolvedId, storedId]);
}

/**
 * Canonical accessor for the active org id. Returns '' when no org is resolved
 * (so callers can gate org-scoped queries with `enabled: !!orgId`). Switching
 * org re-keys the cache and triggers a refetch.
 */
export function useCurrentOrgId(): string {
  const { org } = useCurrentOrg();
  // Compare against the sentinel is not enough: a membership whose id is null or
  // undefined produced a key that was neither NO_ORG_KEY nor a usable id, and it
  // reached the backend stringified as the literal "null" -- Postgres answered
  // `invalid input syntax for type uuid: "null"`, the endpoint 500'd, and the UI
  // told the user "the scan service couldn't complete the probe. Check the
  // target", blaming a target that was fine. Anything that is not a non-empty
  // string is no org.
  if (org.key === NO_ORG_KEY) return '';
  return typeof org.key === 'string' && org.key.length > 0 ? org.key : '';
}

/**
 * The effective role for the ACTIVELY-SELECTED org.
 *
 * Every RBAC gate (nav rails, command/action registry, create menu, role-admin
 * routes) reads role through this hook — NOT `useAuth().role`, which is the
 * user's primary-org role and is wrong for a multi-org user who holds a
 * different role in the selected tenant (#318). Combines the reactive current
 * org id (`useCurrentOrgId`, backed by `useSyncExternalStore`) with the session
 * so switching orgs re-renders and flips the role.
 *
 * Lives here (not in `@shared/auth`) on purpose: `use-current-org` already
 * depends on `@shared/auth`, so combining them here keeps the dependency
 * one-directional and avoids the hook/import cycle that binding the role INTO
 * `useAuth` would create.
 */
export function useCurrentRole(): UserRole | null {
  const { session } = useAuth();
  const orgId = useCurrentOrgId();
  return roleForOrg(session, orgId);
}

/**
 * Refetch all mounted org-scoped queries when the selected org changes.
 *
 * Org-scoped query keys embed orgId, so a switch already points TanStack Query
 * at a different cache entry (no cross-tenant bleed). This hook additionally
 * invalidates the active queries so any cached-but-stale data for the new org
 * is refetched immediately rather than served stale for up to staleTime.
 *
 * Mount once, high in the tree (e.g. DashboardShell).
 */
export function useInvalidateOnOrgChange(): void {
  const orgId = useCurrentOrgId();
  const queryClient = useQueryClient();
  const prevOrgId = useRef(orgId);

  useEffect(() => {
    if (!orgId) return;
    if (prevOrgId.current === orgId) return;
    prevOrgId.current = orgId;
    void queryClient.invalidateQueries({ refetchType: 'active' });
  }, [orgId, queryClient]);
}

/**
 * Plain (non-React) accessor for the currently-selected org id.
 *
 * This is the function `apiClient` (which is NOT a React component) calls to
 * attach `x-organization-id` on every outgoing request. It reads the same
 * module-level `selectedId` that the hooks use, so it's always in sync —
 * including across tabs via the `storage` listener.
 *
 * Returns '' when no org has been selected yet (same semantics as
 * `useCurrentOrgId()` returning '' for the NO_ORG_KEY case).
 */
export function getCurrentOrgId(): string {
  // `selectedId` is kept in sync with localStorage by `setSelectedId` and the
  // cross-tab `storage` listener, so we don't need another localStorage read.
  return selectedId;
}

/**
 * Test-only escape hatch to reset the module store between specs (it is
 * module-global, so it would otherwise leak across cases).
 */
export function __resetCurrentOrgForTests(): void {
  selectedId = '';
  selectedByOtherTab = false;
  emit();
}
